import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { v1 as createUuid } from 'uuid'

import {
  IAttachment,
  IAttachmentInput,
  IPermitTypeField,
  MediaViewType,
} from '~/client/graph/types.generated'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

export enum UploadStatus {
  Uploading = 'uploading',
  Error = 'error',
}

export interface IUploadingItem {
  id: string
  fileName: string
  status: UploadStatus
  file?: File
}

const ACCEPTED_MIME_TYPES =
  'application/pdf,image/jpeg,image/png,video/mp4,video/webm'

export function getFileExtension(fileName: string): string {
  if (!fileName) return ''
  return fileName.split('.').pop()?.toUpperCase() || ''
}

export { ACCEPTED_MIME_TYPES }

export default class InlineMediaConfiguratorStore {
  @observable public field: IPermitTypeField = null
  @observable public uploadingItems: IUploadingItem[] = []
  @observable public editingItemUrl: string | null = null
  @observable public editingName: string = ''
  @observable public isUploadsExpanded: boolean = true
  private editingOriginalExtension: string = ''
  private onChange: (field: IPermitTypeField) => void
  private fileUploadingStore: FileUploadingStore
  private projectId: string

  public constructor(
    field: IPermitTypeField,
    onChange: (field: IPermitTypeField) => void,
    fileUploadingStore: FileUploadingStore,
    projectId: string,
  ) {
    this.field = field
    this.onChange = onChange
    this.fileUploadingStore = fileUploadingStore
    this.projectId = projectId
    makeObservable(this)
  }

  @action.bound
  public setField(field: IPermitTypeField) {
    this.field = field
  }

  @action.bound
  public setOnChangeHandler(onChange: (field: IPermitTypeField) => void) {
    this.onChange = onChange
  }

  @computed
  public get mediaItems(): IAttachment[] {
    return (this.field.inlineMediaItems as IAttachment[]) || []
  }

  @computed
  public get totalCount(): number {
    return this.mediaItems.length
  }

  @computed
  public get hasItems(): boolean {
    return this.totalCount > 0
  }

  @action.bound
  public toggleUploadsExpanded() {
    this.isUploadsExpanded = !this.isUploadsExpanded
  }

  @action.bound
  public handleFileDrop(acceptedFiles: File[]) {
    acceptedFiles.forEach(file => this.uploadFile(file))
  }

  @action.bound
  public async uploadFile(file: File) {
    const id = createUuid()
    const item: IUploadingItem = {
      id,
      fileName: file.name,
      status: UploadStatus.Uploading,
      file,
    }

    this.uploadingItems.push(item)

    try {
      const fileType = this.fileUploadingStore.getAttachmentFileType(file)
      const [result] = await this.fileUploadingStore.uploadFile(
        file,
        fileType,
        this.projectId,
        file.name,
      )

      runInAction(() => {
        if (result) {
          const attachment: IAttachmentInput = {
            fileName: file.name,
            url: result.fileURL,
            size: String(file.size),
          }

          this.addMediaItem(attachment)
          this.removeUploadingItem(id)
        } else {
          this.setUploadError(id)
        }
      })
    } catch {
      runInAction(() => {
        this.setUploadError(id)
      })
    }
  }

  @action.bound
  public retryUpload(id: string) {
    const item = this.uploadingItems.find(i => i.id === id)
    if (item?.file) {
      this.removeUploadingItem(id)
      this.uploadFile(item.file)
    }
  }

  @action.bound
  public removeUploadingItem(id: string) {
    this.uploadingItems = this.uploadingItems.filter(i => i.id !== id)
  }

  @action.bound
  public deleteMediaItem(url: string) {
    const updated = this.mediaItems.filter(item => item.url !== url)
    this.updateField({ inlineMediaItems: updated })
  }

  @action.bound
  public startEditing(url: string) {
    const item = this.mediaItems.find(i => i.url === url)
    if (item) {
      this.editingItemUrl = url
      const dotIndex = item.fileName.lastIndexOf('.')
      if (dotIndex > 0) {
        this.editingName = item.fileName.substring(0, dotIndex)
        this.editingOriginalExtension = item.fileName.substring(dotIndex)
      } else {
        this.editingName = item.fileName
        this.editingOriginalExtension = ''
      }
    }
  }

  @action.bound
  public updateEditingName(name: string) {
    this.editingName = name
  }

  @action.bound
  public cancelEditing() {
    this.editingItemUrl = null
    this.editingName = ''
    this.editingOriginalExtension = ''
  }

  @action.bound
  public saveEditing() {
    if (!this.editingItemUrl || !this.editingName.trim()) {
      this.cancelEditing()
      return
    }

    const newFileName = this.editingName.trim() + this.editingOriginalExtension

    const updated = this.mediaItems.map(item => {
      if (item.url === this.editingItemUrl) {
        return { ...item, fileName: newFileName }
      }
      return item
    })

    this.updateField({ inlineMediaItems: updated })
    this.cancelEditing()
  }

  @action.bound
  public reorderItems(items: IAttachment[]) {
    this.updateField({ inlineMediaItems: items })
  }

  @action.bound
  public updateMediaView(view: MediaViewType) {
    this.updateField({ mediaView: view })
  }

  private setUploadError(id: string) {
    const item = this.uploadingItems.find(i => i.id === id)
    if (item) {
      item.status = UploadStatus.Error
    }
  }

  private addMediaItem(attachment: IAttachmentInput) {
    const items = [...this.mediaItems, attachment as IAttachment]
    this.updateField({ inlineMediaItems: items })
  }

  private updateField(updates: Partial<IPermitTypeField>) {
    this.field = { ...this.field, ...updates } as IPermitTypeField
    this.onChange(this.field)
  }
}
